import {
	ErrorModal,
	getMomentTz,
	HelmetDexteel,
	useMesfRealtime,
} from "@dexteel/mesf-core";
import { Box, Grid2, keyframes, Paper, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProductionChart } from "@/pages/mill/overview-2/components/ProductionChart";
import { UsageMeters } from "@/pages/mill/overview-2/components/UsageMeters";
import { routeDelaysManager } from "@/setup/routes/delay.routes";
import { HoursProgressBar } from "@/shared/components/HoursProgressBar";
import { KPISection } from "@/shared/components/KPISection";
import { pageBgColor } from "@/utils/theme";
import { M1RollingTotalStats } from "./components/M1RollingTotalStats";
import {
	M1CurrentSectionInfo,
	M1UpcomingSectionInfo,
} from "./components/M1SectionInfo";
import { M1ShiftChart } from "./components/M1ShiftChart";
import { M1ShiftStats } from "./components/M1ShiftStats";
import { RollChangeCountdown } from "@/pages/mill/overview-2/components/RollChangeCountdown";
import { ActiveEventChip } from "@/shared/components/ActiveEventChip";
import { M1OverviewResponse } from "./models/M1Overview";
import { getM1Overview } from "./repositories/M1OverviewRepository";
import { StatusIndicator } from "@/pages/mill/overview-2/components/StatusIndicator";

const moment = getMomentTz();

export const useM1Overview = () => {
	return useQuery<M1OverviewResponse, Error>({
		queryKey: ["m1Overview"],
		queryFn: ({ signal }) => getM1Overview(signal),
		refetchOnWindowFocus: true,
		refetchInterval: 30_000,
	});
};

export const M1OverviewPage = () => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [error, setError] = useState("");

	const chartDates = useMemo(() => {
		const end = moment();
		const start = moment();
		return { start: start.toDate(), end: end.toDate() };
	}, []);

	const { data, isLoading, error: e, isError } = useM1Overview();

	useMesfRealtime({
		onReceiveMessage: async (_author, message) => {
			if (
				message.replaceAll("[", "").replaceAll("]", "") === "M1.GetMillOverview"
			) {
				await queryClient.invalidateQueries({ queryKey: ["m1Overview"] });
			}
		},
	});

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	if (isLoading || !data) {
		return null;
	}

	if (isError) {
		return <ErrorModal error={error} onHide={() => setError("")} />;
	}

	return (
		<>
			<HelmetDexteel title="1 Mill Overview" />
			<Box sx={{ p: 1.5, backgroundColor: pageBgColor }}>
				<Grid2 container spacing={1}>
					{/* ── Title row ── */}
					<Grid2 size={{ xs: 12 }}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
							}}
						>
							<Typography variant="h5" sx={{ fontWeight: 600 }}>
								1 Mill Overview
							</Typography>
							<StatusIndicator
								isInDelay={data.IsInDelay}
								onClick={() => {
									if (data?.IsInDelay && data?.CurrentDelayId) {
										navigate(
											`${routeDelaysManager}?delayId=${data.CurrentDelayId}`,
										);
									}
								}}
							/>
							{data.RollChangeCountdown?.ActiveDMHEvents.map((dmh, i) => (
								<ActiveEventChip
									key={`${dmh.EventTypeCode}-${dmh.Start.getTime()}-${i}`}
									eventTypeCode={dmh.EventTypeCode}
									start={dmh.Start}
									remainingMinutes={dmh.RemainingMinutes}
								/>
							))}
							<RollChangeCountdown data={data.RollChangeCountdown} />
							<Typography
								sx={{ fontSize: "0.7rem", color: "#999", fontStyle: "italic" }}
							>
								Work in progress
							</Typography>
						</Box>
					</Grid2>
					{/* ── Top row: 30-day chart + Current Section + Hours Progress Bar ── */}
					<Grid2
						container
						spacing={1}
						size={{ xs: 12 }}
						sx={{ alignItems: "stretch" }}
					>
						<Grid2 size={{ xs: 12, lg: 4 }} sx={{ overflow: "hidden" }}>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									height: "100%",
									pt: "10px",
								}}
							>
								<Typography
									variant="subtitle2"
									sx={{ fontWeight: 700, mb: 0.5 }}
								>
									Production over last 30 days
								</Typography>
								<M1ShiftChart
									start={chartDates.start}
									end={chartDates.end}
									grid={{
										top: 10,
										left: 10,
										containLabel: true,
									}}
									cardHeight={130}
									disableOnClick
								/>
							</Box>
						</Grid2>
						<Grid2 size={{ xs: 12, lg: 4 }}>
							<M1CurrentSectionInfo data={data.CurrentSection} />
						</Grid2>
						<Grid2 size={{ xs: 12, lg: 4 }}>
							<Paper
								elevation={1}
								sx={{
									p: 1,
									border: "2px solid #4CAF50",
									borderRadius: 2,
									minWidth: 0,
									height: "100%",
								}}
							>
								<HoursProgressBar
									eventStart={data.CurrentSection.EventStart}
									scheduledEnd={data.CurrentSection.ScheduledEnd}
									projectedEnd={data.CurrentSection.ProjectedRollOut}
									scheduledHours={data.CurrentSection.ScheduledHours}
									currentHours={data.CurrentSection.CurrentHours}
									remainingHours={data.CurrentSection.RemainingHours}
									varianceHours={data.CurrentSection.VarianceHours}
								/>
							</Paper>
						</Grid2>
					</Grid2>

					<Grid2
						container
						spacing={1}
						size={{ xs: 12 }}
						sx={{ alignItems: "stretch" }}
					>
						<Grid2 size={{ xs: 12, lg: 4 }} sx={{ order: { xs: 2, lg: 1 } }}>
							<M1RollingTotalStats data={data.RollingTotal} />
						</Grid2>
						<Grid2 size={{ xs: 12, lg: 4 }} sx={{ order: { xs: 1, lg: 2 } }}>
							<ProductionChart data={data.ProductionChart} />
						</Grid2>
						<Grid2 size={{ xs: 12, lg: 4 }} sx={{ order: { xs: 3, lg: 3 } }}>
							<Paper
								elevation={1}
								sx={{
									p: 1,
									border: "2px solid #2196F3",
									borderRadius: 2,
									minWidth: 0,
									height: "100%",
								}}
							>
								<M1UpcomingSectionInfo data={data.UpcomingSection} />
							</Paper>
						</Grid2>
					</Grid2>

					{/* ── Shifts + Usage + KPI gauges (aligned by base) ── */}
					<Grid2
						container
						spacing={1}
						size={{ xs: 12 }}
						sx={{ alignItems: "stretch" }}
					>
						<Grid2 size={{ xs: 12, lg: 4 }}>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: 1,
									height: "100%",
								}}
							>
								<M1ShiftStats
									title="Current Shift"
									data={data.CurrentShift}
									showTPHIndicator
								/>

								<M1ShiftStats
									title="Previous Shift"
									data={data.PreviousShift}
									showTPHIndicator
								/>

								<Box
									sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
								>
									<Typography
										variant="subtitle2"
										sx={{ fontWeight: 600, mb: 1 }}
									>
										Usage Meters
									</Typography>
									<Box sx={{ flexGrow: 1 }}>
										<UsageMeters data={data.UsageMeters} />
									</Box>
								</Box>
							</Box>
						</Grid2>

						<Grid2 size={{ xs: 12, lg: 8 }}>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: 1,
									height: "100%",
								}}
							>
								<KPISection title="Current Shift" data={data.CurrentShiftKPI} />
								<KPISection title="Rolling Total" data={data.RollingTotalKPI} />
							</Box>
						</Grid2>
					</Grid2>
				</Grid2>
			</Box>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
